(() => {
  class BundleLandingPage {
    constructor(root) {
      this.root = root;
      this.tabs = Array.from(root.querySelectorAll("[data-bundle-tab-trigger]"));
      this.panels = Array.from(root.querySelectorAll("[data-bundle-tab-panel]"));
      this.cards = Array.from(root.querySelectorAll("[data-bundle-card]"));
      this.summary = root.querySelector("[data-bundle-summary]");
      this.errorNodes = Array.from(root.querySelectorAll("[data-bundle-error]"));
      this.submitButtons = Array.from(root.querySelectorAll("[data-bundle-submit]"));
      this.mobileSlots = root.querySelector("[data-mobile-slots]");

      this.minItems = Number(root.dataset.minItems || 2);
      this.maxItems = Number(root.dataset.maxItems || 2);
      this.discountType = root.dataset.discountType || "percentage";
      this.discountPercentage = Number(root.dataset.discountPercentage || 15);
      this.fixedBundlePrice = Number(root.dataset.fixedBundlePrice || 0);

      this.selection = [null, null];

      this.bindTabs();
      this.setupCardDropdowns();
      this.bindCards();
      this.bindSubmit();
      this.renderMobileSlots();
      this.updateSummary();
    }

    bindTabs() {
      this.tabs.forEach((tab) => {
        tab.addEventListener("click", () => {
          const idx = tab.dataset.tabIndex;
          this.tabs.forEach((t) => {
            const active = t.dataset.tabIndex === idx;
            t.classList.toggle("is-active", active);
            t.setAttribute("aria-selected", active ? "true" : "false");
          });
          this.panels.forEach((panel) => {
            const active = panel.dataset.tabIndex === idx;
            panel.classList.toggle("is-active", active);
            panel.hidden = !active;
          });
        });
      });
    }

    parseVariants(card) {
      const script = card.querySelector("[data-card-variants]");
      if (!script) return [];
      try {
        return JSON.parse(script.textContent || "[]");
      } catch (_) {
        return [];
      }
    }

    setupCardDropdowns() {
      this.cards.forEach((card) => {
        const variants = this.parseVariants(card).filter((v) => Boolean(v.available));
        const select = card.querySelector("[data-card-size]");
        if (!select) return;

        const byOption = new Map();
        variants.forEach((variant) => {
          const label = String(variant.option1 || variant.title || "").trim();
          if (!label) return;
          if (!byOption.has(label)) byOption.set(label, variant);
        });

        byOption.forEach((variant, label) => {
          const option = document.createElement("option");
          option.value = String(variant.id);
          option.textContent = label;
          option.dataset.variantTitle = variant.title || label;
          select.appendChild(option);
        });

        if (!byOption.size) select.disabled = true;

        select.addEventListener("change", () => {
          card.classList.toggle("has-size", Boolean(select.value));
          const isSelected = this.selection.some(
            (item) => item && item.productId === Number(card.dataset.productId)
          );
          if (isSelected) {
            // If already selected and size changes, auto-replace with new variant.
            const variant = variants.find((v) => String(v.id) === String(select.value));
            if (variant) this.assignCard(card, variant);
          }
        });
      });
    }

    bindCards() {
      this.cards.forEach((card) => {
        const addButton = card.querySelector("[data-card-add]");
        const select = card.querySelector("[data-card-size]");
        const variants = this.parseVariants(card);

        if (!addButton) return;
        if (!addButton.dataset.defaultLabel) {
          addButton.dataset.defaultLabel = addButton.textContent.trim();
        }

        addButton.addEventListener("click", () => {
          const productId = Number(card.dataset.productId);
          const currentlySelected = this.selection.some(
            (item) => item && item.productId === productId
          );

          // Toggle behavior: if selected, same button deselects.
          if (currentlySelected) {
            this.removeByProductId(productId);
            this.setError("");
            return;
          }

          if (!select || !select.value) {
            this.setError("Please choose a size first.");
            return;
          }

          const variant = variants.find((v) => String(v.id) === String(select.value));
          if (!variant) {
            this.setError("Selected size is unavailable.");
            return;
          }

          this.assignCard(card, variant);
          this.setError("");
        });
      });
    }

    bindSubmit() {
      this.submitButtons.forEach((button) => {
        button.addEventListener("click", () => this.addBundleToCart());
      });
    }

    assignCard(card, variant) {
      const entry = {
        productId: Number(card.dataset.productId),
        title: card.dataset.productTitle || "Product",
        image: card.dataset.productImage || "",
        variantId: Number(variant.id),
        variantTitle: variant.title || "",
        price: Number(variant.price || 0),
      };

      const existingIndex = this.selection.findIndex((item) => item && item.productId === entry.productId);
      if (existingIndex !== -1) {
        this.selection[existingIndex] = entry;
      } else {
        const emptyIndex = this.selection.findIndex((item) => item === null);
        if (emptyIndex !== -1) this.selection[emptyIndex] = entry;
        else this.selection[1] = entry;
      }

      this.refreshCardStates();
      this.updateSummary();
    }

    removeByProductId(productId) {
      const index = this.selection.findIndex((item) => item && item.productId === productId);
      if (index === -1) return;
      this.selection[index] = null;
      this.refreshCardStates();
      this.updateSummary();
    }

    removeSlot(index) {
      if (index < 0 || index > 1) return;
      this.selection[index] = null;
      this.refreshCardStates();
      this.updateSummary();
    }

    refreshCardStates() {
      this.cards.forEach((card) => {
        const id = Number(card.dataset.productId);
        const selected = this.selection.some((item) => item && item.productId === id);
        card.classList.toggle("is-selected", selected);

        const addButton = card.querySelector("[data-card-add]");
        if (addButton) {
          addButton.textContent = selected ? "Deselect" : (addButton.dataset.defaultLabel || "Select");
        }
      });
    }

    money(cents) {
      const value = Number(cents || 0) / 100;
      return `Rs ${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    getTotals() {
      const subtotal = this.selection.reduce((sum, item) => sum + (item ? item.price : 0), 0);
      let discount = 0;
      let total = subtotal;

      const filled = this.selection.filter(Boolean).length;
      const validWindow = filled >= this.minItems && filled <= this.maxItems;

      if (validWindow) {
        if (this.discountType === "fixed_bundle" && this.fixedBundlePrice > 0) {
          total = Math.max(0, Math.round(this.fixedBundlePrice * 100));
          discount = Math.max(0, subtotal - total);
        } else {
          discount = Math.round((subtotal * this.discountPercentage) / 100);
          total = Math.max(0, subtotal - discount);
        }
      }

      return { subtotal, discount, total, filled, validWindow };
    }

    renderSlot(slotNode, item, index) {
      if (!slotNode) return;
      if (!item) {
        const fallback = slotNode.dataset.slot === "0" ? "Select your first t-shirt..." : "Select your second t-shirt...";
        const text = slotNode.dataset.slot === "0"
          ? this.root.querySelector("[data-slot='0'] .bundle-summary__placeholder p")?.textContent || fallback
          : this.root.querySelector("[data-slot='1'] .bundle-summary__placeholder p")?.textContent || fallback;
        slotNode.innerHTML = `<div class=\"bundle-summary__placeholder\"><span>+</span><p>${text}</p></div>`;
        return;
      }

      const image = item.image ? `<img src=\"${item.image}\" alt=\"${item.title}\">` : "";
      slotNode.innerHTML = `
        <div class="bundle-summary__item">
          ${image}
          <div>
            <p class="bundle-summary__item-title">${item.title}</p>
            <p class="bundle-summary__item-meta">${item.variantTitle}</p>
          </div>
          <button type="button" class="bundle-summary__remove" data-remove-slot="${index}" aria-label="Remove item">×</button>
        </div>
      `;

      const removeButton = slotNode.querySelector("[data-remove-slot]");
      if (removeButton) removeButton.addEventListener("click", () => this.removeSlot(index));
    }

    renderMobileSlots() {
      if (!this.mobileSlots) return;
      this.mobileSlots.innerHTML = "";
      for (let i = 0; i < 2; i += 1) {
        const slot = document.createElement("div");
        slot.className = "bundle-mobile-bar__slot";
        this.mobileSlots.appendChild(slot);
      }
    }

    updateMobileSlots() {
      if (!this.mobileSlots) return;
      const slots = Array.from(this.mobileSlots.children);
      slots.forEach((slot, index) => {
        const item = this.selection[index];
        if (item && item.image) slot.innerHTML = `<img src=\"${item.image}\" alt=\"${item.title}\">`;
        else slot.textContent = "+";
      });
    }

    updateSummary() {
      const slot0 = this.summary?.querySelector("[data-slot='0']");
      const slot1 = this.summary?.querySelector("[data-slot='1']");
      this.renderSlot(slot0, this.selection[0], 0);
      this.renderSlot(slot1, this.selection[1], 1);

      const totals = this.getTotals();


      this.submitButtons.forEach((btn) => {
        btn.disabled = !totals.validWindow;
      });

      this.updateMobileSlots();
    }

    setError(message) {
      this.errorNodes.forEach((node) => {
        node.textContent = message || "";
        node.hidden = !message;
      });
    }

    async addBundleToCart() {
      const totals = this.getTotals();
      if (!totals.validWindow) {
        this.setError(`Please select exactly ${this.maxItems} items.`);
        return;
      }

      const items = this.selection
        .filter(Boolean)
        .map((entry) => ({
          id: entry.variantId,
          quantity: 1,
          properties: { Bundle: this.root.querySelector(".bundle-page__title")?.textContent || "Bundle" },
        }));

      if (!items.length) {
        this.setError("Please select products to continue.");
        return;
      }

      this.setError("");
      this.submitButtons.forEach((btn) => { btn.disabled = true; });

      const cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
      const payload = { items };

      if (cart && typeof cart.getSectionsToRender === "function") {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === "function") cart.setActiveElement(document.activeElement);
      }

      try {
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        config.body = JSON.stringify(payload);

        const response = await fetch(routes.cart_add_url, config);
        const data = await response.json();

        if (data.status) {
          this.setError(data.description || "Unable to add bundle to cart.");
          if (typeof publish === "function" && window.PUB_SUB_EVENTS?.cartError) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: "bundle-page",
              errors: data.errors || data.description,
              message: data.message,
            });
          }
          return;
        }

        if (!cart) {
          window.location.href = window.routes?.cart_url || "/cart";
          return;
        }

        if (typeof publish === "function" && window.PUB_SUB_EVENTS?.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: "bundle-page",
            cartData: data,
          });
        }

        if (typeof cart.renderContents === "function") {
          cart.renderContents(data);
        }
      } catch (error) {
        console.error(error);
        this.setError("Something went wrong. Please try again.");
      } finally {
        if (cart && cart.classList && cart.classList.contains("is-empty")) {
          cart.classList.remove("is-empty");
        }
        this.updateSummary();
      }
    }
  }

  const init = () => {
    document.querySelectorAll("[data-bundle-page]").forEach((root) => {
      if (root.dataset.bundlePageReady === "true") return;
      root.dataset.bundlePageReady = "true";
      new BundleLandingPage(root);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
